import React, { createContext, useCallback, useContext, useMemo } from "react";
import { getVisibilityKeys } from "../../utils/visibilityState";
import { deleteVal, getVal, setVal } from "oolib";
import { useQueryClient } from "react-query";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetAllOptionsForDimension1 } from "../utils/useGetAllOptionsForDimension1";
import { isValidDimension2For } from "../utils/useGetAllOptionsForDimension2";
import { useGroupedSubThemeOptions } from "../utils/useGroupedSubThemeOptions";
import { useTopicClusteringDimensionOptions } from "../utils/useTopicClusteringDimensionOptions";
import { useArithmeticExpression } from "../../utils/ArithmeticExpressionInput/useArithmeticExpression";
import { getDerivedDimensionTitle } from "../utils/_extractWidgetTitle";
import { getWidgetContentTypes, pruneStaleWidgetFilters } from "../../utils/getWidgetContentTypes";
import { DATA_KEYS } from "./DATA_KEYS";
import { useReportLandingContext } from "../../../../ReportLandingContext";

const BarChartConfigContext = createContext(null);

/** Wipe a dimension's visibility state (mode + dynamic settings + manual list). */
const clearVisibilityState = (w, dimension) => {
  const keys = getVisibilityKeys({ dataKeys: DATA_KEYS, dimension });
  [keys.modeKey, keys.dynamicPresetKey].forEach((k) => {
    w = deleteVal(w, k);
  });
  return w;
};

/** Exchange the values at two config paths (either may be unset). */
const swapVals = (w, pathA, pathB) => {
  const a = getVal(w, pathA);
  const b = getVal(w, pathB);
  w = deleteVal(w, pathA);
  w = deleteVal(w, pathB);
  if (b !== undefined) w = setVal(w, pathA, b);
  if (a !== undefined) w = setVal(w, pathB, a);
  return w;
};

/**
 * Move each slot's per-field settings to the other slot: show/hide state
 * (manual list, mode, dynamic preset, N) and the number-field configuration
 * (representation, custom ranges, buckets, bucket size, histogram mode). They
 * belong to the field, so when the fields swap slots they swap with them.
 */
const swapDimensionSettings = (w) => {
  const k1 = getVisibilityKeys({ dataKeys: DATA_KEYS, dimension: 1 });
  const k2 = getVisibilityKeys({ dataKeys: DATA_KEYS, dimension: 2 });
  ["hiddenKey", "nKey", "legacyPresetKey", "modeKey", "dynamicPresetKey"].forEach((name) => {
    w = swapVals(w, k1[name], k2[name]);
  });
  ["CustomRanges", "NumberOfBuckets", "BucketSize", "NumberFieldRepresentation", "HistogramBucketMode"].forEach((suffix) => {
    w = swapVals(w, DATA_KEYS[`dimension1${suffix}`], DATA_KEYS[`dimension2${suffix}`]);
  });
  return w;
};

export const useBarChartConfig = () => {
  const ctx = useContext(BarChartConfigContext);
  if (!ctx) throw new Error("useBarChartConfig must be used within BarChartConfigProvider");
  return ctx;
};

export const BarChartConfigProvider = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
  showNumberInputsInOptions = true,
  showDimension2Input = true,
  dimensionsInputPropsConfig = {},
  children,
}) => {
  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");
  const { SELF_MANAGED_BASE_CONFIGS, annotationTagsCount } = useGetQueryData("platformConfigs");
  // Clear this widget's cached aggregation data on a dimension change so the
  // Show/Hide value list doesn't keep showing the previous dimension's bars.
  const { clearWidgetDataStore } = useReportLandingContext() || {};
  const groupedSubThemeOptions = useGroupedSubThemeOptions();
  const topicClusteringOptions = useTopicClusteringDimensionOptions();

  // --- updateWidget helper (same pattern as MetricCard) ---
  const updateWidget = useCallback((updaterFn) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const widgetType = widgetConfig?.comp || 'WidgetBarChart';
      const prevContentTypes = getWidgetContentTypes(widgetConfig, widgetType);
      widgetConfig = updaterFn(widgetConfig);
      // A write that changes which datasets the widget reads drops the widget
      // filters that belonged to a dataset it left (see pruneStaleWidgetFilters).
      widgetConfig = pruneStaleWidgetFilters(widgetConfig, prevContentTypes, widgetType);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  // --- Options ---
  const { options: optionsForDim1 } = useGetAllOptionsForDimension1({ showNumberInputsInOptions });

  // --- Derived ---
  const dim1IsGroupQuestion = useMemo(() => {
    const dim1Value = getVal(thisWidgetConfig, DATA_KEYS.dimension1);
    return dim1Value?.comp === 'GroupQuestionsInputSingle';
  }, [thisWidgetConfig]);

  const widgetType = thisWidgetConfig?.comp || 'WidgetBarChart';
  const contentTypesForFilters = useMemo(() => {
    return getWidgetContentTypes(thisWidgetConfig, widgetType);
  }, [thisWidgetConfig, widgetType]);

  // --- Handlers ---
  const handleDimension1Change = useCallback((v) => {
    updateWidget((w) => {
      // The breakdown survives a data-field change as long as it's still a
      // valid option for the new field; otherwise it and its settings go.
      const keepDim2 = isValidDimension2For({
        dimension2Value: getVal(w, DATA_KEYS.dimension2),
        dimension1Value: v,
        allTpls,
        isSelfManagedTenant: SELF_MANAGED_BASE_CONFIGS?.enable === true,
        annotationTagsCount,
        smTagTypesConfig: SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null,
        groupedSubThemeOptions,
        topicClusteringOptions,
      });

      w = setVal(w, DATA_KEYS.dimension1, v);
      w = deleteVal(w, DATA_KEYS.dimension1CustomRanges);
      w = deleteVal(w, DATA_KEYS.dimension1NumberOfBuckets);
      w = deleteVal(w, DATA_KEYS.dimension1BucketSize);
      w = deleteVal(w, DATA_KEYS.dimension1NumberFieldRepresentation);
      w = deleteVal(w, DATA_KEYS.dimension1HistogramBucketMode);
      w = deleteVal(w, DATA_KEYS.hiddenDim1Datapoints);
      w = deleteVal(w, DATA_KEYS.dim1VisibilityPreset);
      w = deleteVal(w, DATA_KEYS.dim1VisibilityN);
      w = clearVisibilityState(w, 1);
      if (!keepDim2) {
        w = deleteVal(w, DATA_KEYS.dimension2);
        w = deleteVal(w, DATA_KEYS.dimension2CustomRanges);
        w = deleteVal(w, DATA_KEYS.dimension2NumberOfBuckets);
        w = deleteVal(w, DATA_KEYS.dimension2BucketSize);
        w = deleteVal(w, DATA_KEYS.dimension2NumberFieldRepresentation);
        w = deleteVal(w, DATA_KEYS.dimension2HistogramBucketMode);
        w = deleteVal(w, DATA_KEYS.hiddenDim2Datapoints);
        w = deleteVal(w, DATA_KEYS.dim2VisibilityPreset);
        w = deleteVal(w, DATA_KEYS.dim2VisibilityN);
        w = clearVisibilityState(w, 2);
      }

      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        const dim2 = keepDim2 ? getVal(w, DATA_KEYS.dimension2) : null;
        w = setVal(w, DATA_KEYS.field1, getDerivedDimensionTitle(v, dim2) || '');
      }
      return w;
    });
    // New dimension → drop cached data so the Show/Hide value list rebuilds from
    // the new dimension's result instead of the previous one.
    clearWidgetDataStore?.(thisWidgetConfig?.id);
  }, [updateWidget, clearWidgetDataStore, thisWidgetConfig?.id, allTpls, SELF_MANAGED_BASE_CONFIGS, annotationTagsCount, groupedSubThemeOptions, topicClusteringOptions]);

  const handleDimension2Change = useCallback((k, v) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS.dimension2, v);
      w = deleteVal(w, DATA_KEYS.dimension2CustomRanges);
      w = deleteVal(w, DATA_KEYS.dimension2NumberOfBuckets);
      w = deleteVal(w, DATA_KEYS.dimension2BucketSize);
      w = deleteVal(w, DATA_KEYS.dimension2NumberFieldRepresentation);
      w = deleteVal(w, DATA_KEYS.dimension2HistogramBucketMode);
      w = deleteVal(w, DATA_KEYS.hiddenDim2Datapoints);
      w = deleteVal(w, DATA_KEYS.dim2VisibilityPreset);
      w = deleteVal(w, DATA_KEYS.dim2VisibilityN);
      w = clearVisibilityState(w, 2);

      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        const dim1 = getVal(w, DATA_KEYS.dimension1);
        const newTitle = getDerivedDimensionTitle(dim1, v);
        if (newTitle) {
          w = setVal(w, DATA_KEYS.field1, newTitle);
        }
      }
      return w;
    });
    // Breakdown changed → drop cached data so the Show/Hide value list rebuilds.
    clearWidgetDataStore?.(thisWidgetConfig?.id);
  }, [updateWidget, clearWidgetDataStore, thisWidgetConfig?.id]);

  const handleFlipDimensions = useCallback(() => {
    const dim1 = getVal(thisWidgetConfig, DATA_KEYS.dimension1);
    const dim2 = getVal(thisWidgetConfig, DATA_KEYS.dimension2);
    if (!dim1 || !dim2) return;

    updateWidget((w) => {
      // Swap dimension values
      w = setVal(w, DATA_KEYS.dimension1, dim2);
      w = setVal(w, DATA_KEYS.dimension2, dim1);

      // Every per-field setting (show/hide, number representation, buckets,
      // custom ranges) follows its field into the other slot.
      w = swapDimensionSettings(w);

      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        const newTitle = getDerivedDimensionTitle(dim2, dim1);
        if (newTitle) {
          w = setVal(w, DATA_KEYS.field1, newTitle);
        }
      }
      return w;
    });
    // Dimensions swapped → drop cached data so the Show/Hide value list rebuilds.
    clearWidgetDataStore?.(thisWidgetConfig?.id);
  }, [thisWidgetConfig, updateWidget, clearWidgetDataStore]);

  // --- Arithmetic expression state (shared, same pattern as MetricCard) ---
  const dim1Value = getVal(thisWidgetConfig, DATA_KEYS.dimension1);

  const arithmeticState = useArithmeticExpression({
    allOptions: optionsForDim1,
    value: dim1Value,
    onChange: handleDimension1Change,
    disabled: false,
  });

  // --- Context value ---
  const value = useMemo(() => ({
    widgetConfig: thisWidgetConfig,
    updateWidget,
    dataKeys: DATA_KEYS,
    setConfig,
    underEditWidgetIdx,
    // Options
    optionsForDim1,
    allTpls,
    SELF_MANAGED_BASE_CONFIGS,
    annotationTagsCount,
    contentTypesForFilters,
    // Config flags
    showDimension2Input,
    dimensionsInputPropsConfig,
    // Derived
    dim1IsGroupQuestion,
    // Arithmetic
    arithmeticState,
    // Handlers
    handleDimension1Change,
    handleDimension2Change,
    handleFlipDimensions,
  }), [
    thisWidgetConfig, updateWidget, setConfig, underEditWidgetIdx,
    optionsForDim1, allTpls, SELF_MANAGED_BASE_CONFIGS, annotationTagsCount,
    contentTypesForFilters, showDimension2Input, dimensionsInputPropsConfig,
    dim1IsGroupQuestion, arithmeticState,
    handleDimension1Change, handleDimension2Change, handleFlipDimensions,
  ]);

  return (
    <BarChartConfigContext.Provider value={value}>
      {children}
    </BarChartConfigContext.Provider>
  );
};
